import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import {
	type BondPaymentType,
	type BondTrackerTab,
	type BulkAclEntity,
	type CustomAttributeValueDto,
	type EditorSelectableBasketsRequestEntityEnum,
	type InvestmentCommentaryDTO,
	type InvestmentStatus,
	type ListInvestmentRequest,
	type MaturityAndCouponResponse,
	type PortfolioMetricsOrderingResponse,
	type ProjectedListInvestmentRequest,
	type ReportHorizon,
	AclEntityMinInfoEntityTypeEnum,
	EntityEditorControllerApiFactory,
	ExportCommentarySourceEnum,
	InvestmentBulkBulkStaticConfigurationControllerV1ApiFactory,
	InvestmentBulkEnhancementConfigurationControllerV4ApiFactory,
	InvestmentControllerV4ApiFactory,
	InvestmentCreationConfigurationControllerV4ApiFactory,
	InvestmentDraftConfigurationControllerV4ApiFactory,
	InvestmentEnhancementConfigurationControllerV4ApiFactory,
	InvestmentEnhancementControllerV4ApiFactory,
	InvestmentEnhancementReportsControllerApiFactory,
	InvestmentExportControllerApiFactory,
	InvestmentReportsControllerApiFactory,
	InvestmentSmartXSyncsControllerV4ApiFactory,
	InvestmentsStaticConfigurationControllerApiFactory,
	PdfControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import type { Abortable } from "@mdotm/utils/promise";
import debounce from "debounce";
import { match } from "ts-pattern";
import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import type { MultiStepPortfolioContext } from "$root/components/Portfolio/CreatePortfolio/shared";
import type { EditMulti_MultiStepPortfolioContext } from "$root/components/Portfolio/CreatePortfolio/shared-multi";
import { type EventPayloadMap, eventBus } from "$root/event-bus";
import { formatDate } from "$root/localization/formatters";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { downloadContentDisposition, downloadFileResponse, minutes, seconds } from "$root/utils";
import type { MaturityCouponTrackerResponse } from "$root/widgets-architecture/widgets/MaturityCouponTracker/data";

const portfolioRepositoryBaseKeys = {
	list: "investmentList",
	projectedList: "investmentProjectedList",
};

const { newQuery, newAction, invalidateEntity, invalidateEntities, invalidateLists } = repositoryHelperFactory(
	"portfolio",
	{
		listKeys: Object.values(portfolioRepositoryBaseKeys),
	},
);

const configCreateInvestmentApi = () => getApiGen(InvestmentCreationConfigurationControllerV4ApiFactory);
const configDraftInvestmentApi = () => getApiGen(InvestmentDraftConfigurationControllerV4ApiFactory);
const configEnhanceInvestmentApi = () => getApiGen(InvestmentEnhancementConfigurationControllerV4ApiFactory);
const configStaticInvestmentApi = () => getApiGen(InvestmentsStaticConfigurationControllerApiFactory);
const enhancementReportsApi = () => getApiGen(InvestmentEnhancementReportsControllerApiFactory);
const reportsApi = () => getApiGen(InvestmentReportsControllerApiFactory);

const bulkEnhancementApi = () => getApiGen(InvestmentBulkEnhancementConfigurationControllerV4ApiFactory);
const investmentBulkBulkStaticV1Api = () => getApiGen(InvestmentBulkBulkStaticConfigurationControllerV1ApiFactory);

const investmentApi = () => getApiGen(InvestmentControllerV4ApiFactory);
const enhancementApi = () => getApiGen(InvestmentEnhancementControllerV4ApiFactory);
const exportApi = () => getApiGen(InvestmentExportControllerApiFactory);
const smartXSyncsApi = () => getApiGen(InvestmentSmartXSyncsControllerV4ApiFactory);

const editorApi = () => getApiGen(EntityEditorControllerApiFactory);

const pdfControllerApi = () => getApiGen(PdfControllerApiFactory);

// The tracker endpoint's generated types are all-optional; normalize into the widget's non-optional
// shape so downstream transforms don't have to defend against missing fields.
/** @internal — exported for tests */
export function toMaturityCouponTrackerResponse(response: MaturityAndCouponResponse): MaturityCouponTrackerResponse {
	// TODO(MDOT-25403): the per-row € amounts are in no generated client yet — the contract is
	// unsettled (CORE-872) — so the entries are widened locally with the guessed field names.
	// Replace the widening with the generated type once the backend ships them.
	const bonds = (response.bonds ?? []) as Array<
		NonNullable<MaturityAndCouponResponse["bonds"]>[number] & { maturedValue?: number | null }
	>;
	const coupons = (response.coupons ?? []) as Array<
		NonNullable<MaturityAndCouponResponse["coupons"]>[number] & { couponAmount?: number | null }
	>;
	return {
		buckets: (response.buckets ?? []).map((bucket) => ({
			id: bucket.id ?? "",
			weight: bucket.weight ?? 0,
		})),
		bonds: bonds.map((bond) => ({
			name: bond.name ?? "",
			expirationDate: bond.expirationDate ?? null,
			maturedValue: bond.maturedValue ?? null,
			bucketId: bond.bucketId ?? "",
		})),
		coupons: coupons.map((coupon) => ({
			name: coupon.name ?? "",
			couponDate: coupon.couponDate ?? null,
			couponRate: coupon.couponRate ?? null,
			couponAmount: coupon.couponAmount ?? null,
			bucketId: coupon.bucketId ?? "",
		})),
	};
}

export const portfolioRepository = {
	invalidateEntity,
	invalidateLists,

	summary: newQuery("summary", (uuid: string, opts?: { enhanced: boolean; asOf?: string }) =>
		axiosExtract(
			opts?.enhanced
				? enhancementReportsApi().getInvestmentEnhancementSummary(uuid)
				: reportsApi().getInvestmentSummary(uuid, opts?.asOf),
		),
	),

	comparisonDetails: newQuery(
		"comparisonDetails",
		(uuid: string) => axiosExtract(investmentApi().getPortfolioComparisonDetails([uuid])).then((x) => x[0]),
		{
			cacheTime: minutes(5),
			staleTime: minutes(5),
		},
	),

	projectedList: newQuery(portfolioRepositoryBaseKeys.projectedList, (params?: ProjectedListInvestmentRequest) =>
		axiosExtract(investmentApi().getProjectedInvestmentList(params)).then((list) =>
			list.sort(builtInSortFnFor("creationTime", "desc")),
		),
	),

	paginatedList: newQuery(portfolioRepositoryBaseKeys.list, (params: ListInvestmentRequest) =>
		axiosExtract(investmentApi().listInvestments(params)),
	),

	rename: newAction(async (uuid: string, name: string) => {
		await axiosExtract(investmentApi().renameInvestment(uuid, name));

		invalidateEntity(uuid);
	}),

	duplicate: newAction(async (uuid: string, name: string) => {
		const clone = await axiosExtract(investmentApi().duplicateInvestment(uuid, name));

		invalidateLists();
		return clone;
	}),

	delete: newAction(async (uuid: string) => {
		await axiosExtract(investmentApi().deleteInvestment(uuid));

		invalidateLists();
	}),

	isInvestmentNameAvailable: newAction((uuid: string, opts?: Abortable) => {
		return axiosExtract(investmentApi().isInvestmentNameAvailable(uuid, opts));
	}),

	createCommentaryFromTemplate: newAction(
		async ({ uuid, name }: { uuid?: string; name?: string }, opts: { templateUuid: string; enhanced: boolean }) => {
			await axiosExtract(
				opts.enhanced
					? enhancementApi().createCommentaryForEnhancementWithTemplate(uuid!, opts.templateUuid)
					: investmentApi().createCommentaryFromTemplateUUID(uuid!, opts.templateUuid),
			);
			if (opts.enhanced) {
				trackMixPanelEvent("portfolio.entityActions.generateProposalCommentary", {
					portfolio: { uuid: uuid!, name: name! },
				});
			} else {
				trackMixPanelEvent("portfolio.entityActions.generateCommentary", {
					portfolio: { uuid: uuid!, name: name! },
				});
			}

			invalidateEntity(uuid!);
		},
	),

	regenerateCommentary: newAction(async ({ uuid, name }: { uuid?: string; name?: string }) => {
		await axiosExtract(investmentApi().regenerateCommentary(uuid!));
		trackMixPanelEvent("portfolio.entityActions.regenerateCommentary", {
			portfolio: { uuid: uuid!, name: name! },
		});

		invalidateEntity(uuid!);
	}),

	// Exports the commentary as a Word document. `enhanced` selects the proposal vs current snapshot via the
	// `source` query param — there is no enhanced export factory for commentary (unlike composition), the
	// single endpoint switches on `source`. Downloads via content-disposition headers, so the raw axios
	// response is passed straight to `downloadContentDisposition` (no `axiosExtract`).
	exportCommentary: newAction(
		async ({ uuid, name }: { uuid?: string; name?: string }, opts: { enhanced: boolean; asOf?: string }) => {
			const response = await exportApi().exportCommentary(
				uuid!,
				opts.enhanced ? ExportCommentarySourceEnum.Proposal : ExportCommentarySourceEnum.Current,
				opts.asOf,
				{ responseType: "blob" },
			);
			await downloadContentDisposition(response);

			if (opts.enhanced) {
				trackMixPanelEvent("portfolio.entityAction.downloadProposalCommentary", {
					portfolio: { uuid: uuid!, name: name! },
				});
			} else {
				trackMixPanelEvent("portfolio.entityAction.downloadCommentary", {
					portfolio: { uuid: uuid!, name: name! },
				});
			}
		},
	),

	deleteCustomMetrics: newAction(async (uuid: string) => {
		await axiosExtract(investmentApi().deleteCustomMetrics(uuid));

		invalidateLists();
	}),

	/**
	 * Makes the portfolio's snapshot for a past date available, generating it when it does not exist
	 * yet (MDOT-24860). Answers READY — every as-of read below can be issued — or GENERATING, in which
	 * case they would 404 and the page waits for the `investment-report-update` event carrying this
	 * same `asOf`. Safe to call repeatedly: an already-computed snapshot costs the backend one indexed
	 * read, and a generation still in flight is joined rather than restarted.
	 *
	 * An action rather than a query, though it answers with data, because `POST`ing it can commission
	 * a multi-minute backend job — and every convenience a query brings is a way to commission one by
	 * accident. As a query it inherited three retries (which re-ask a backend that has already
	 * refused), a stale time (which re-asks on any remount), and invalidation by `invalidateEntity`
	 * (which made a cache sweep start a job). Callers ask on purpose; `useAsOfSnapshot` owns when.
	 */
	asOfSnapshot: newAction((uuid: string, asOf: string) =>
		axiosExtract(reportsApi().ensurePortfolioSnapshot(uuid, asOf)),
	),

	historyEntries: newQuery("historyEntries", (uuid: string, opts?: { asOf?: string }) => {
		return axiosExtract(reportsApi().getInvestmentActionHistorySummary(uuid, opts?.asOf));
	}),

	deleteHistoryEntryNote: newAction(async (uuid: string, historyItem: { uuid: string }) => {
		await axiosExtract(investmentApi().removeInvestmentNote(uuid, historyItem.uuid));

		invalidateEntity(uuid);
	}),

	editHistoryEntryNote: newAction(async (uuid: string, historyItem: { uuid: string; note: string }) => {
		await axiosExtract(
			investmentApi().editInvestmentNote({
				investmentUuid: uuid,
				historyUuid: historyItem.uuid,
				noteText: historyItem.note,
			}),
		);

		invalidateEntity(uuid);
	}),

	createBulkCommentary: newAction(async (investments: Array<InvestmentCommentaryDTO>) => {
		await investmentApi().createCommentaryBulkFromTemplate(investments);

		invalidateEntities(investments.map(({ investmentUUID }) => investmentUUID!));
	}),

	toggleExecution: newAction(async (uuid: string) => {
		await enhancementApi().updateExecuteState(uuid);

		invalidateEntity(uuid);
	}),

	/**
	 * Lower bound for a backdated accept over a batch of portfolios: the most recent
	 * `max(proposalCreation, lastTradeDate)` across them, i.e. the earliest day that is
	 * valid for *every* portfolio. Portfolios that are unauthorized, unknown or not
	 * proposal-ready are excluded from the bound by the backend.
	 */
	acceptLowerBound: newQuery("acceptLowerBound", (investmentUuids: string[]) =>
		axiosExtract(enhancementApi().getAcceptLowerBound({ investmentUuids })),
	),

	acceptEnhancement: newAction(async (uuid: string, acceptAsDate?: string) => {
		await enhancementApi().acceptEnhancement(uuid, acceptAsDate);

		invalidateEntity(uuid);
	}),

	/**
	 * Accepts the pending proposal of many portfolios at one shared date (omit `acceptAsDate`
	 * for today). Per-portfolio failures — missing permission included — are reported in
	 * `acceptErrors` rather than aborting the batch.
	 */
	bulkAcceptEnhancement: newAction(async (uuids: string[], acceptAsDate?: string) => {
		try {
			const { successfullyAccepted, acceptErrors } = await axiosExtract(
				enhancementApi().bulkAcceptEnhancement({
					investmentUuids: uuids,
					date: acceptAsDate,
				}),
			);

			return {
				successfullyAccepted: successfullyAccepted ?? [],
				acceptErrors: acceptErrors ?? [],
			};
		} finally {
			// An unexpected server error aborts the batch with no body, yet the portfolios accepted
			// before the failure stay committed — so refresh whatever the outcome.
			invalidateEntities(uuids);
		}
	}),

	/**
	 * Sends the pending proposal of many portfolios to execution in one request — the bulk
	 * counterpart of {@link toggleExecution}, hitting the same execute-state switch. Per-portfolio
	 * failures come back in `executionErrors` rather than aborting the batch.
	 */
	bulkUpdateExecuteState: newAction(async (uuids: string[]) => {
		try {
			const { successfullyExecuted, executionErrors } = await axiosExtract(
				enhancementApi().bulkUpdateExecuteState(uuids),
			);

			return { successfullyExecuted: successfullyExecuted ?? [], executionErrors: executionErrors ?? [] };
		} finally {
			// The switch is applied portfolio by portfolio server-side, so an aborted batch can still
			// leave some of them executing — refresh whatever the outcome.
			invalidateEntities(uuids);
		}
	}),

	rejectEnhancement: newAction(async (uuid: string) => {
		await enhancementApi().rejectEnhancement(uuid);

		invalidateEntity(uuid);
	}),

	fixVar: newAction(async (uuid: string) => {
		await enhancementApi().fixVar(uuid);

		invalidateEntity(uuid);
	}),

	wizardMainInfo: newQuery("mainInfo", (area: MultiStepPortfolioContext["area"]) =>
		match(area)
			.with({ name: "enhance" }, (x) =>
				axiosExtract(configEnhanceInvestmentApi().getEnhancementConfigurationSelectableMainInfo(x.portfolioUid)),
			)
			.with({ name: "settings-enhanced" }, (x) =>
				axiosExtract(configStaticInvestmentApi().getStaticConfigurationSelectableMainInfo(x.portfolioUid)),
			)
			.with({ name: "settings-current" }, (x) =>
				axiosExtract(configStaticInvestmentApi().getStaticConfigurationSelectableMainInfo(x.portfolioUid)),
			)
			.with({ name: "create" }, () =>
				axiosExtract(configCreateInvestmentApi().getCreationConfigurationSelectableMainInfo()),
			)
			.with({ name: "draft" }, (x) =>
				axiosExtract(configDraftInvestmentApi().getDraftConfigurationSelectableMainInfo(x.portfolioUid)),
			)
			.exhaustive(),
	),

	wizardInvestableUniverses: newQuery("investableUniverses", (area: MultiStepPortfolioContext["area"]) =>
		match(area)
			.with({ name: "enhance" }, (x) =>
				axiosExtract(
					configEnhanceInvestmentApi().getEnhancementConfigurationSelectableInvestableUniverses(x.portfolioUid),
				),
			)
			.with({ name: "settings-enhanced" }, (x) =>
				axiosExtract(configStaticInvestmentApi().getStaticConfigurationSelectableInvestableUniverses(x.portfolioUid)),
			)
			.with({ name: "settings-current" }, (x) =>
				axiosExtract(configStaticInvestmentApi().getStaticConfigurationSelectableInvestableUniverses(x.portfolioUid)),
			)
			.with({ name: "create" }, () =>
				axiosExtract(configCreateInvestmentApi().getCreationConfigurationSelectableInvestableUniverses()),
			)
			.with({ name: "draft" }, (x) =>
				axiosExtract(configDraftInvestmentApi().getDraftConfigurationSelectableInvestableUniverses(x.portfolioUid)),
			)
			.exhaustive(),
	),

	wizardInvestableUniverse: newQuery(
		"investableUniverse",
		(area: MultiStepPortfolioContext["area"], universeIdentifier: string) =>
			match(area)
				.with({ name: "create" }, () =>
					axiosExtract(configCreateInvestmentApi().loadCreationConfigurationInvestableUniverse(universeIdentifier)),
				)
				.with({ name: "enhance" }, (x) =>
					axiosExtract(
						configEnhanceInvestmentApi().loadEnhancementConfigurationInvestableUniverse(
							x.portfolioUid,
							universeIdentifier,
						),
					),
				)
				.with({ name: "draft" }, (x) =>
					axiosExtract(
						configDraftInvestmentApi().loadDraftConfigurationInvestableUniverse(x.portfolioUid, universeIdentifier),
					),
				)
				.with({ name: "settings-enhanced" }, (x) =>
					axiosExtract(
						configStaticInvestmentApi().loadStaticConfigurationInvestableUniverse(x.portfolioUid, universeIdentifier),
					),
				)
				.with({ name: "settings-current" }, (x) =>
					axiosExtract(
						configStaticInvestmentApi().loadStaticConfigurationInvestableUniverse(x.portfolioUid, universeIdentifier),
					),
				)
				.exhaustive(),
	),

	wizardAllocationSelectableClassifications: newQuery(
		"allocationSelectableClassification",
		(area: MultiStepPortfolioContext["area"], opts: { classificationUuid: string; universeIdentifier?: string }) =>
			match(area)
				.with({ name: "enhance" }, (x) =>
					axiosExtract(
						configEnhanceInvestmentApi().getAllocationSelectableCustomTags1(x.portfolioUid, opts.classificationUuid),
					),
				)
				.with({ name: "settings-enhanced" }, (x) =>
					axiosExtract(
						configStaticInvestmentApi().getAllocationSelectableCustomTags(
							x.portfolioUid,
							opts.classificationUuid,
							opts.universeIdentifier,
						),
					),
				)
				.with({ name: "settings-current" }, (x) =>
					axiosExtract(
						configStaticInvestmentApi().getAllocationSelectableCustomTags(
							x.portfolioUid,
							opts.classificationUuid,
							opts.universeIdentifier,
						),
					),
				)
				.with({ name: "create" }, () =>
					axiosExtract(configCreateInvestmentApi().getAllocationSelectableCustomTags3(opts.classificationUuid)),
				)
				.with({ name: "draft" }, (x) =>
					axiosExtract(
						configDraftInvestmentApi().getAllocationSelectableCustomTags2(x.portfolioUid, opts.classificationUuid),
					),
				)
				.exhaustive(),
	),

	wizardSelectableMarketViews: newQuery(
		"selectableMarketViews",
		(area: MultiStepPortfolioContext["area"] | EditMulti_MultiStepPortfolioContext["area"]) =>
			match(area)
				.with({ name: "create-bulk-proposal" }, () =>
					axiosExtract(bulkEnhancementApi().getEnhancementConfigurationSelectableMarketViews1()),
				)
				.with({ name: "edit-bulk-settings" }, () =>
					axiosExtract(investmentBulkBulkStaticV1Api().getEnhancementConfigurationSelectableMarketViews2()),
				)
				.with({ name: "enhance" }, (x) =>
					axiosExtract(configEnhanceInvestmentApi().getEnhancementConfigurationSelectableMarketViews(x.portfolioUid)),
				)
				.with({ name: "settings-enhanced" }, (x) =>
					axiosExtract(configStaticInvestmentApi().getStaticConfigurationSelectableMarketViews(x.portfolioUid)),
				)
				.with({ name: "settings-current" }, (x) =>
					axiosExtract(configStaticInvestmentApi().getStaticConfigurationSelectableMarketViews(x.portfolioUid)),
				)
				.with({ name: "create" }, () =>
					axiosExtract(configCreateInvestmentApi().getCreationConfigurationSelectableMarketViews()),
				)
				.with({ name: "draft" }, (x) =>
					axiosExtract(configDraftInvestmentApi().getDraftConfigurationSelectableMarketViews(x.portfolioUid)),
				)
				.exhaustive(),
	),

	wizardMarketViewConfiguration: newQuery(
		"loadMarketViewConfiguration",
		(
			area: MultiStepPortfolioContext["area"] | EditMulti_MultiStepPortfolioContext["area"],
			opt: { scenarioId: string; custom?: boolean },
		) =>
			match(area)
				.with({ name: "create-bulk-proposal" }, (x) =>
					axiosExtract(
						bulkEnhancementApi().loadEnhancementConfigurationMarketView1(x.proposalUuid, opt.scenarioId, opt.custom),
					),
				)
				.with({ name: "edit-bulk-settings" }, (x) =>
					axiosExtract(
						investmentBulkBulkStaticV1Api().loadStaticConfigurationMarketView1(x.bulkUid, opt.scenarioId, opt.custom),
					),
				)
				.with({ name: "enhance" }, (x) =>
					axiosExtract(
						configEnhanceInvestmentApi().loadEnhancementConfigurationMarketView(
							x.portfolioUid,
							opt.scenarioId,
							opt.custom,
						),
					),
				)
				.with({ name: "settings-enhanced" }, (x) =>
					axiosExtract(
						configStaticInvestmentApi().loadStaticConfigurationMarketView(x.portfolioUid, opt.scenarioId, opt.custom),
					),
				)
				.with({ name: "settings-current" }, (x) =>
					axiosExtract(
						configStaticInvestmentApi().loadStaticConfigurationMarketView(x.portfolioUid, opt.scenarioId, opt.custom),
					),
				)
				.with({ name: "create" }, () =>
					axiosExtract(configCreateInvestmentApi().loadCreationConfigurationMarketView(opt.scenarioId, opt.custom)),
				)
				.with({ name: "draft" }, (x) =>
					axiosExtract(
						configDraftInvestmentApi().loadDraftConfigurationMarketView(x.portfolioUid, opt.scenarioId, opt.custom),
					),
				)
				.exhaustive(),
	),

	getACL: newQuery("acl", (uuid: string) => axiosExtract(investmentApi().getAclEditStarter1(uuid))),

	smartXStatus: newQuery("smartXStatus", (uuid: string) => axiosExtract(smartXSyncsApi().getStatus(uuid))),

	updateACL: newAction(async (uuid: string, acl: Array<RichAccessControl>) => {
		await investmentApi().setACL1(uuid, acl);
		invalidateEntity(uuid);
	}),

	composition: newQuery(
		"composition",
		(
			uuid: string,
			opts: {
				enhanced: boolean;
				currentlyContainsNestedPortfolios: boolean;
				flattenNestedPortfolios: boolean;
				asOf?: string;
			},
		) => {
			return opts.currentlyContainsNestedPortfolios && !opts.flattenNestedPortfolios
				? axiosExtract(
						opts.enhanced
							? enhancementReportsApi().getInvestmentEnhancementAggregateComposition(uuid)
							: reportsApi().getInvestmentAggregateComposition(uuid, opts.asOf),
					)
				: axiosExtract(
						opts.enhanced
							? enhancementReportsApi().getInvestmentComposition1(uuid)
							: reportsApi().getInvestmentComposition(uuid, opts.asOf),
					);
		},
	),

	// The Maturity & Coupon tracker has a dedicated endpoint returning its buckets (with portfolio
	// weights) plus the bonds/coupons bucketed within them.
	maturityCouponTracker: newQuery(
		"maturityCouponTracker",
		(uuid: string, opts?: { asOf?: string }): Promise<MaturityCouponTrackerResponse> =>
			axiosExtract(reportsApi().getMaturityAndCoupons(uuid, opts?.asOf)).then(toMaturityCouponTrackerResponse),
	),

	// Server-paginated bond/dividend cash-flow events (coupons, dividends, maturities) for the
	// Bonds & Dividends tracker. Every filter (tab, page window, date range, search, type) is a
	// query param, so it flows into the query key and refetches; `keepPreviousData` avoids flicker.
	bondCashFlows: newQuery(
		"bondCashFlows",
		(params: {
			uuid: string;
			tab: BondTrackerTab;
			offset: number;
			limit: number;
			fromDate?: string;
			toDate?: string;
			searchName?: string;
			types?: BondPaymentType[];
			asOf?: string;
		}) =>
			axiosExtract(
				reportsApi().getBondsAndDividends(
					params.uuid,
					params.asOf,
					params.offset,
					params.limit,
					params.toDate,
					params.fromDate,
					params.searchName,
					params.tab,
					params.types,
				),
			),
		{ keepPreviousData: true },
	),

	commentary: newQuery(
		"commentary",
		(uuid: string, opts: { enhanced: boolean /* used for refetch */; status: InvestmentStatus; asOf?: string }) =>
			axiosExtract(
				opts.enhanced
					? enhancementReportsApi().getCommentaries1(uuid ?? "")
					: reportsApi().getCommentaries(uuid ?? "", opts.asOf),
			),
	),

	performance: newQuery("performance", (uuid: string, opts: { benchmarkId: string | null; asOf?: string }) =>
		opts.benchmarkId
			? axiosExtract(reportsApi().getInvestmentPerformance(uuid, opts.benchmarkId, opts.asOf))
			: Promise.resolve(null),
	),

	exposure: newQuery(
		"exposure",
		(
			uuid: string,
			opts: { benchmarkId: string | null; secondLevel: string | null; enhanced: boolean; asOf?: string },
		) =>
			!uuid || !opts.benchmarkId || !opts.secondLevel
				? Promise.resolve(null)
				: opts.enhanced
					? axiosExtract(
							enhancementReportsApi().getTwoLevelsInvestmentExposure1(uuid, opts.benchmarkId, opts.secondLevel),
						)
					: axiosExtract(
							reportsApi().getTwoLevelsInvestmentExposure(uuid, opts.benchmarkId, opts.secondLevel, opts.asOf),
						),
	),

	exposureDecomposition: newQuery("exposureDecomposition", (uuid: string, benchmarkId: string, enhanced: boolean) =>
		enhanced
			? axiosExtract(enhancementReportsApi().getExposureDecomposition1(uuid, benchmarkId))
			: axiosExtract(reportsApi().getExposureDecomposition(uuid, benchmarkId)),
	),

	factorsExposure: newQuery(
		"factorsExposure",
		(uuid: string, opts: { enhanced: boolean; benchmarkId: string; asOf?: string }) =>
			opts.enhanced
				? axiosExtract(enhancementReportsApi().getInvestmentFactors1(uuid, opts.benchmarkId))
				: axiosExtract(reportsApi().getInvestmentFactors(uuid, opts.benchmarkId, opts.asOf)),
	),

	monitoring: newQuery("monitoring", (uuid: string, opts: { enhanced: boolean; asOf?: string }) =>
		axiosExtract(
			!opts.enhanced
				? reportsApi().getMonitoringMetrics(uuid, opts.asOf)
				: enhancementReportsApi().getMonitoringMetrics1(uuid),
		),
	),

	metrics: newQuery(
		"metrics",
		(
			uuid: string,
			opts: { benchmarkId: string | null; preferences: PortfolioMetricsOrderingResponse; asOf?: string },
		) =>
			opts.benchmarkId
				? axiosExtract(reportsApi().getPortfolioMetrics(uuid, opts.benchmarkId, opts.preferences, opts.asOf))
				: Promise.resolve(null),
	),

	attribution: newQuery(
		"attribution",
		(uuid: string, opts: { benchmarkId: string | null; horizon: ReportHorizon; asOf?: string }) =>
			opts.benchmarkId
				? axiosExtract(reportsApi().getRealizedPerformanceAttribution(uuid, opts.benchmarkId, opts.horizon, opts.asOf))
				: Promise.resolve(null),
	),

	volatilityContribution: newQuery(
		"volatilityContribution",
		(uuid: string, opts: { benchmarkId: string | null; horizon: ReportHorizon; asOf?: string }) =>
			opts.benchmarkId
				? axiosExtract(reportsApi().getRealizedVolatilityContribution(uuid, opts.benchmarkId, opts.horizon, opts.asOf))
				: Promise.resolve(null),
	),
	performanceContribution: newQuery(
		"performanceContribution",
		(uuid: string, opts: { benchmarkId: string | null; horizon: ReportHorizon; asOf?: string }) =>
			opts.benchmarkId
				? axiosExtract(reportsApi().getRealizedPerformanceContribution(uuid, opts.benchmarkId, opts.horizon, opts.asOf))
				: Promise.resolve(null),
	),

	exanteContributionVolatility: newQuery(
		"exanteContributionVolatility",
		(uuid: string, opts: { enhanced: boolean; benchmarkId: string | null; horizon: ReportHorizon; asOf?: string }) =>
			opts.enhanced
				? axiosExtract(enhancementReportsApi().getCompositionVolatilityContribution1(uuid, opts.horizon))
				: opts.benchmarkId
					? axiosExtract(
							reportsApi().getCompositionVolatilityContribution(uuid, opts.benchmarkId, opts.horizon, opts.asOf),
						)
					: Promise.resolve(null),
	),

	exanteMetrics: newQuery(
		"exanteMetrics",
		(uuid: string, opts: { enhanced: boolean; benchmarkId: string | null; asOf?: string }) =>
			opts.benchmarkId
				? opts.enhanced
					? axiosExtract(enhancementReportsApi().getPortfolioExAnteMetrics1(uuid, opts.benchmarkId))
					: axiosExtract(reportsApi().getPortfolioExAnteMetrics(uuid, opts.benchmarkId, opts.asOf))
				: Promise.resolve(null),
	),

	editorAvailablePortfolios: newQuery(
		"editorAvailablePortfolios",
		(
			uuid: string | null,
			entity: Parameters<ReturnType<typeof EntityEditorControllerApiFactory>["getEditorNewSelectablePortfolios"]>[0],
		) =>
			axiosExtract(
				uuid
					? editorApi().getEditorEditSelectablePortfolios(uuid, entity)
					: editorApi().getEditorNewSelectablePortfolios(entity),
			).then((x) => x.selectablePortfolios),
		{ cacheTime: minutes(5), staleTime: minutes(5) },
	),

	downloadReportZip: newAction(async (zipId: string, opts?: { prefix?: string }) => {
		const zip = await axiosExtract(pdfControllerApi().downloadReportsById(zipId));
		downloadFileResponse(zip, {
			fileName: `${opts?.prefix ? `${opts?.prefix}_` : ""}Reports_${formatDate(new Date())}.zip`,
		});
	}),

	verifyInvestmentNormalizedComposition: newQuery(
		"verifyInvestmentNormalizedComposition",
		async (params: { investmentUuid: string; weight: number; entity: EditorSelectableBasketsRequestEntityEnum }) => {
			const { removedTickers } = await axiosExtract(
				editorApi().verifyEditorPortfolio(params.investmentUuid, params.weight, params.entity),
			);
			return removedTickers ?? [];
		},
	),

	bulkAclPreview: newQuery("bulkAclPreview", (acl: Array<BulkAclEntity>) =>
		axiosExtract(investmentApi().getBulkACLCheck1(acl)),
	),
	bulkShare: newAction(async (bulkAclEntities: Array<BulkAclEntity>) => {
		await axiosExtract(investmentApi().setBulkACL1(bulkAclEntities));
		invalidateEntities(bulkAclEntities.map((entity) => entity.entityUuid!));
	}),

	updateCustomAttributes: newAction(async (uuid: string, customAttributeValueDto: CustomAttributeValueDto[]) => {
		await axiosExtract(investmentApi().updateCustomAttributes(uuid, customAttributeValueDto));
	}),

	deleteCustomAttribute: newAction(async (uuid: string) => {
		await axiosExtract(investmentApi().deleteCustomMetrics(uuid, true));
	}),

	removeCustomAttributes: newAction(async (uuid: string, customAttributeValueDto: CustomAttributeValueDto[]) => {
		await axiosExtract(investmentApi().removeCustomAttributes(uuid, customAttributeValueDto));
	}),
} as const;

// portfolioRepository.rename({ uuid: "akghriugho", name: "rtgherigo" });
// const {useSummaryQuery}=portfolioRepository;
// const {summary: {useQuery as useSummaryQuery}}=portfolioRepository;
// const {summary}=portfolioRepository;
// summary.useQuery({ uuid: "u4398ry49rh", enhanced: false });

eventBus.addListener(
	"investment-update",
	debounce((e: EventPayloadMap["investment-update"]) => {
		if (e.type === "PORTFOLIO_UPDATE") {
			invalidateEntity(e.uuid ?? "");
		}
	}, seconds(2)),
);

eventBus.addListener(
	"shared-entity",
	debounce((e: EventPayloadMap["shared-entity"]) => {
		if (e.sharedEntityType === AclEntityMinInfoEntityTypeEnum.Investment) {
			invalidateEntity(e.sharedEntityUuid ?? "");
		}
	}, seconds(2)),
);

eventBus.addListener(
	"investment-report-update",
	debounce((e: EventPayloadMap["investment-report-update"]) => {
		invalidateEntity(e.uuid ?? "");
	}, seconds(2)),
);

eventBus.addListener(
	"commentary-update",
	debounce((e: EventPayloadMap["commentary-update"]) => {
		invalidateEntity(e.uuid ?? "");
	}, seconds(2)),
);

eventBus.addListener(
	"shared-entities",
	debounce((e: EventPayloadMap["shared-entities"]) => {
		const referenceEntities = e.investments?.filter((x) => x.sharedEntityType === "INVESTMENT");
		if (referenceEntities?.length && referenceEntities?.length > 0) {
			invalidateEntities(referenceEntities.flatMap((x) => (x.sharedEntityUuid ? [x.sharedEntityUuid] : [])));
		}
	}, seconds(2)),
);
